import { KeyValue } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    model,
    ModelSignal,
    OnInit,
    Signal,
    signal,
    untracked,
    WritableSignal,
} from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { debounceTime, distinctUntilChanged, firstValueFrom, Subscription } from 'rxjs';

import { DepositFolderListBloc } from '@connect-services/client/blocs/deposit-folder-list.bloc';
import { MapFilterBloc } from '@connect-services/client/blocs/map-filter.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ConfirmationModal } from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import { TitleComponent } from '@connect-services/client/components/field-intervention-title/title/title.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { CompositionAttributeFilterFormComponent } from '@connect-services/client/pages/filter/layer/create-layer-filter/composition-attribute-filter-form/composition-attribute-filter-form.component';
import {
    DepositLayerFilterFormComponent,
    SearchParams,
} from '@connect-services/client/pages/filter/layer/create-layer-filter/deposit-layer-filter-form/deposit-layer-filter-form.component';
import { FilterPanelHeaderComponent } from '@connect-services/client/pages/filter/layer/create-layer-filter/filter-panel-header/filter-panel-header.component';
import { LayerAttributeFilterFormComponent } from '@connect-services/client/pages/filter/layer/create-layer-filter/layer-attribute-filter-form/layer-attribute-filter-form.component';
import {
    ContentLayoutService,
    VERTICAL_HOME_GRID_PARAMS,
} from '@connect-services/client/services/content-layout.service';
import { LayerService } from '@connect-services/client/services/layer.service';
import { MapFilterService } from '@connect-services/client/services/map-filter.service';
import { MapFilterCompositionService } from '@connect-services/client/services/map-filter-composition.service';
import { ProjectEntityTableService } from '@connect-services/client/services/project-entity-table.service';
import { SecurityService } from '@connect-services/client/services/security.service';
import { ValidatorsService } from '@connect-services/client/services/validators.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { ExpansionPanelComponent } from '@connect-services/src/lib/components/expansion-panel/expansion-panel.component';
import { SelectOption } from '@connect-services/src/lib/components/select/select.component';
import {
    DepositFolder,
    DepositFolderControllerGet200Response,
    DepositFolderControllerGetRequestParams,
    DepositFoldersService,
    Layer,
    LayerAttribute,
    LayerAttributeCondition,
    MapFilter,
    MapFilterPreviewDto,
    MapFilterService as SdkMapFilterService,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { OrderEnum, paginationMaxTake } from '@shared/client/models/pagination.model';
import { FormService } from '@shared/client/services/form.service';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

export class LayerAttributeForm extends FormGroup<{
    column: FormControl<string>;
    comparator: FormControl<LayerAttributeCondition.ComparatorEnum | undefined>;
    id: FormControl<string>;
    value: FormControl<string>;
}> {}

export class LayerFilterCreationEditionForm extends FormGroup<{
    conditions: FormGroup<{
        composition: FormControl<string>;
        depositFolders: FormControl<string[]>;
        layerAttributes: FormArray<LayerAttributeForm>;
        layers: FormControl<string[]>;
    }>;
    isShared: FormControl<boolean>;
    name: FormControl<string>;
}> {}

function hasAtLeastOneDepositFolderOrLayerValidator(
    control: AbstractControl<LayerFilterCreationEditionForm>,
): ValidationErrors | null {
    function getValueLength(control: AbstractControl<LayerFilterCreationEditionForm>, key: string): number {
        return (control.get('conditions')!.get(key)!.value as string[]).length;
    }

    if (!getValueLength(control, 'depositFolders') && !getValueLength(control, 'layers')) {
        return {
            depositFolderOrLayerMandatory: true,
        };
    }

    return null;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        CompositionAttributeFilterFormComponent,
        DepositLayerFilterFormComponent,
        ExpansionPanelComponent,
        FilterPanelHeaderComponent,
        FormsModule,
        LayerAttributeFilterFormComponent,
        ReactiveFormsModule,
        RessourcesModule,
        TitleComponent,
    ],
    selector: 'app-create-edit-layer-filter',
    styleUrl: './create-edit-layer-filter.component.scss',
    templateUrl: './create-edit-layer-filter.component.html',
})
export class CreateEditLayerFilterComponent implements OnInit {
    public readonly isEditDisabled: WritableSignal<boolean> = signal(false);

    protected readonly buttonDisableReasons: WritableSignal<string> = signal<string>('');
    protected readonly ButtonType = ButtonType;
    protected readonly defaultNumberElementsDisplayed: number = 8;
    protected readonly depositFolderListBloc = inject(DepositFolderListBloc);
    protected readonly depositFolders: WritableSignal<KeyValue<string, string>[]> = signal([]);
    protected readonly depositFolderTotalCount: WritableSignal<number> = signal(0);
    protected readonly displayedLayers: WritableSignal<KeyValue<string, string>[]> = signal<KeyValue<string, string>[]>(
        [],
    );
    protected readonly filterForm: WritableSignal<LayerFilterCreationEditionForm | null> = signal(null);
    protected readonly formReset: WritableSignal<number> = signal(0);
    protected readonly formService = inject(FormService);
    protected readonly isDepositFoldersExpanded: WritableSignal<boolean> = signal<boolean>(false);
    protected readonly isEditMode: WritableSignal<boolean> = signal<boolean>(false);
    protected readonly isLayerAttributesExpanded: ModelSignal<boolean> = model<boolean>(false);
    protected readonly isLayersExpanded: WritableSignal<boolean> = signal<boolean>(false);
    protected readonly layerAttributes: WritableSignal<LayerAttribute[]> = signal<LayerAttribute[]>([]);
    protected readonly layerAttributesOptions: Signal<SelectOption<string>[]> = computed(() =>
        this.layerAttributes().map((attribute: LayerAttribute): SelectOption<string> => ({
            label: attribute.column,
            value: attribute.column,
        })),
    );
    protected readonly layers: WritableSignal<KeyValue<string, string>[]> = signal([]);
    protected readonly layerTableName: Signal<string | null> = computed(() => {
        const selectedLayers: string[] = this.layersControl.value;
        if (selectedLayers?.length !== 1) {
            return null;
        }

        return this.layersByName().get(selectedLayers[0])?.[0]?.tableName ?? null;
    });
    protected readonly mapFilterCompositionService = inject(MapFilterCompositionService);
    protected readonly mapFilterService = inject(MapFilterService);
    protected readonly panelWidthTwoThird: Signal<boolean> = computed(
        () => this.contentLayoutService.mapSizeType() === VERTICAL_HOME_GRID_PARAMS.ONE_THIRD_VERTICAL,
    );

    private readonly contentLayoutService = inject(ContentLayoutService);
    private readonly depositFoldersService = inject(DepositFoldersService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly formBuilder = inject(FormBuilder);
    private readonly layersByName: WritableSignal<Map<string, Layer[]>> = signal(new Map<string, Layer[]>());
    private readonly layerService = inject(LayerService);
    private readonly mapFilterBloc = inject(MapFilterBloc);
    private readonly matDialog = inject(MatDialog);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectEntityTableService = inject(ProjectEntityTableService);
    private readonly router = inject(Router);
    private readonly sdkMapFilterService = inject(SdkMapFilterService);
    private readonly securityService = inject(SecurityService);
    private readonly snackBarService = inject(SnackBarService);
    private readonly subscriptions: Subscription[] = [];
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        this.destroyRef.onDestroy(() => {
            this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
            this.mapFilterService.clearPreview();
        });

        effect(async () => {
            if (!this.mapFilterBloc.value()) {
                return;
            }

            await untracked(async () => {
                this.createFilterForm();

                await this.expendPanelsForExistingFilter();
            });
        });
    }

    protected get compositionControl(): FormControl<string> {
        return this.filterForm()!.controls.conditions.controls.composition;
    }

    protected get depositFoldersControl(): FormControl<string[]> {
        return (this.filterForm()!.get('conditions') as FormGroup)!.get('depositFolders') as FormControl<string[]>;
    }

    protected get layerAttributesFormArray(): FormArray<LayerAttributeForm> {
        return (this.filterForm()!.get('conditions') as FormGroup)!.get(
            'layerAttributes',
        ) as FormArray<LayerAttributeForm>;
    }

    protected get layersControl(): FormControl<string[]> {
        return (this.filterForm()!.get('conditions') as FormGroup)!.get('layers') as FormControl<string[]>;
    }

    public ngOnInit(): void {
        this.depositFolders.set(
            this.depositFolderListBloc.value()!.items.map((folder: DepositFolder) => ({
                key: folder.name,
                value: folder.id,
            })),
        );
        this.depositFolderTotalCount.set(this.depositFolderListBloc.value()!.totalItems);

        this.createFilterForm();

        this.filterForm()?.statusChanges.subscribe(() => {
            this.setButtonDisableReasons();
        });

        this.setButtonDisableReasons();

        this.loadGroupedLayers();
    }

    protected addNewAttribute(): void {
        this.addLayerAttributeForm();
    }

    protected async goToPreviousPage(): Promise<void> {
        this.mapFilterService.clearPreview();

        const path = [RoutePathEnum.PROJECTS, this.projectBloc.value()?.id];
        await this.router.navigate(path);
    }

    protected async preview(): Promise<void> {
        if (this.layerAttributesFormArray.length >= 2) {
            await this.mapFilterCompositionService.checkCompositionIfNeeded();

            if (!this.mapFilterCompositionService.isCompositionCheckedAndValid()) {
                return;
            }
        }

        const mapFilterPreview: MapFilterPreviewDto = {
            conditions: this.filterForm()!.get('conditions')!.value,
            project: this.projectBloc.value()!.id,
        };

        await this.mapFilterService.preview(mapFilterPreview);
        await this.refreshTableAfterPreview();
    }

    protected removeLayerAttributeForm(index: number): void {
        this.layerAttributesFormArray.removeAt(index);

        for (let i = index; i < this.layerAttributesFormArray.length; i++) {
            const newIndex = i + 1;

            this.layerAttributesFormArray.at(i).get('id')?.setValue(newIndex.toString());
        }

        this.layerAttributesFormArray.updateValueAndValidity({ emitEvent: true, onlySelf: false });

        this.updateCompositionRequirement();
    }

    protected async save(): Promise<void> {
        if (this.layerAttributesFormArray.length >= 2) {
            await this.mapFilterCompositionService.checkCompositionIfNeeded();
        }

        if (
            this.layerAttributesFormArray.length >= 2 &&
            !this.mapFilterCompositionService.isCompositionCheckedAndValid()
        ) {
            return;
        }

        if (this.isEditMode()) {
            await firstValueFrom(
                this.sdkMapFilterService.mapFilterControllerPatch({
                    id: this.mapFilterBloc.value()!.id,
                    updateMapFilterDto: {
                        conditions: this.filterForm()!.get('conditions')!.value,
                        isShared: this.filterForm()!.get('isShared')!.value,
                        name: this.filterForm()!.get('name')!.value,
                    },
                }),
            );
            this.filterForm()!.markAsPristine();
            this.snackBarService.openSuccessSnackBar('mapFilter.updateSuccessful', {
                value: this.filterForm()!.get('name')!.value,
            });
            await this.mapFilterService.initData();

            return;
        }

        await firstValueFrom(
            this.sdkMapFilterService.mapFilterControllerPost({
                createMapFilterDto: {
                    conditions: this.filterForm()!.get('conditions')!.value,
                    isShared: this.filterForm()!.get('isShared')!.value,
                    name: this.filterForm()!.get('name')!.value,
                    project: this.projectBloc.value()!.id,
                },
            }),
        );

        await this.mapFilterService.initData();

        const dialogRef = this.matDialog.open(ConfirmationModal, {
            autoFocus: false,
            data: {
                cancelIcon: ['fas', 'plus'],
                cancelLabel: this.translocoService.translate('mapFilter.createFilter'),
                confirmLabel: this.translocoService.translate('mapFilter.goBackToMap'),
                title: this.translocoService.translate('mapFilter.creationSuccessful', {
                    value: this.filterForm()!.get('name')!.value,
                }),
                type: ModalType.SUCCESS,
            },
            width: '30vw',
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result === ModalResultEnum.CONFIRM) {
                this.router.navigate([RoutePathEnum.PROJECTS, this.projectBloc.value()!.id]);
            }

            this.filterForm!.update(
                (form: LayerFilterCreationEditionForm | null): LayerFilterCreationEditionForm | null => {
                    if (form) {
                        form.reset();
                    }

                    return form;
                },
            );

            this.formReset.update((value: number): number => value + 1);
        });
    }

    protected async searchDeposit(searchParams: SearchParams): Promise<void> {
        if (typeof searchParams.value === 'string' && searchParams.value.length) {
            await this.loadAllDepositFolders(searchParams.value);

            return;
        }

        if (searchParams.showAll) {
            await this.loadAllDepositFolders();

            return;
        }

        this.depositFolders.set(
            this.depositFolderListBloc.value()!.items.map((depositFolder: DepositFolder) => ({
                key: depositFolder.name,
                value: depositFolder.id,
            })),
        );

        return;
    }

    protected searchLayers(searchParams: SearchParams): void {
        if (typeof searchParams.value !== 'string' || searchParams.value.length) {
            this.toggleLayerShowAll(searchParams.showAll);

            return;
        }

        const lowerCaseSearch = searchParams.value.toLowerCase();
        this.displayedLayers.set(
            this.layers().filter((layer: KeyValue<string, string>) =>
                layer.key.toLocaleLowerCase().includes(lowerCaseSearch),
            ),
        );
    }

    protected async toggleDepositFoldersShowAll(showAll: boolean): Promise<void> {
        if (!showAll) {
            this.depositFolders.set(
                this.depositFolderListBloc.value()!.items.map((depositFolder: DepositFolder) => ({
                    key: depositFolder.name,
                    value: depositFolder.id,
                })),
            );

            return;
        }
        await this.loadAllDepositFolders();
    }

    protected toggleLayerShowAll(showAll: boolean): void {
        this.displayedLayers.set(
            this.layers().slice(0, showAll ? this.layers().length : this.defaultNumberElementsDisplayed),
        );
    }

    private addLayerAttributeForm(): void {
        const newAttributeIndex: string = (this.layerAttributesFormArray.length + 1).toString();

        this.layerAttributesFormArray.push(
            this.formBuilder.group({
                column: this.formBuilder.control<string>('', {
                    nonNullable: true,
                    validators: [Validators.required],
                }),
                comparator: this.formBuilder.control<LayerAttributeCondition.ComparatorEnum | undefined>(undefined, {
                    nonNullable: true,
                    validators: [Validators.required],
                }),
                id: this.formBuilder.control<string>(newAttributeIndex, { nonNullable: true }),
                value: this.formBuilder.control<string>('', {
                    nonNullable: true,
                    validators: [Validators.required, Validators.minLength(1)],
                }),
            }),
        );

        this.updateCompositionRequirement();
    }

    private createFilterForm(): void {
        const filter = this.mapFilterBloc.value();

        this.filterForm.set(
            this.formBuilder.group(
                {
                    conditions: this.formBuilder.group({
                        composition: this.formBuilder.control<string>(filter?.conditions.composition ?? '', {
                            nonNullable: true,
                        }),
                        depositFolders: this.formBuilder.control<string[]>(filter?.conditions.depositFolders ?? [], {
                            nonNullable: true,
                        }),
                        layerAttributes: this.formBuilder.array<LayerAttributeForm>(
                            filter?.conditions.layerAttributes?.map((attributeCondition: LayerAttributeCondition) =>
                                this.formBuilder.group({
                                    column: this.formBuilder.control<string>(attributeCondition.column!, {
                                        nonNullable: true,
                                        validators: [Validators.required],
                                    }),
                                    comparator: this.formBuilder.control<
                                        LayerAttributeCondition.ComparatorEnum | undefined
                                    >(attributeCondition.comparator!, {
                                        nonNullable: true,
                                        validators: [Validators.required],
                                    }),
                                    id: this.formBuilder.control<string>(attributeCondition.id!, { nonNullable: true }),
                                    value: this.formBuilder.control<string>(attributeCondition.value!, {
                                        nonNullable: true,
                                        validators: [Validators.required, Validators.minLength(2)],
                                    }),
                                }),
                            ) ?? [],
                        ),
                        layers: this.formBuilder.control<string[]>(filter?.conditions.layers ?? [], {
                            nonNullable: true,
                        }),
                    }),
                    isShared: this.formBuilder.control<boolean>(filter?.isShared ?? false, {
                        nonNullable: true,
                    }),
                    name: this.formBuilder.control<string>(filter?.name ?? '', {
                        nonNullable: true,
                        validators: ValidatorsService.stringValidationForm({
                            maxLength: FormService.VARCHAR_MEDIUM_LENGTH,
                            minLength: FormService.VARCHAR_MIN_LENGTH,
                        }),
                    }),
                },
                {
                    validators: [hasAtLeastOneDepositFolderOrLayerValidator],
                },
            ),
        );

        this.disableEditFormIfNotOwned();

        this.createFormValueChangeEvents();

        this.updateCompositionRequirement();

        this.mapFilterCompositionService.attachCompositionControl(this.compositionControl);
    }

    private createFormValueChangeEvents(): void {
        const depositFoldersControlSubscription = this.depositFoldersControl.valueChanges
            .pipe(debounceTime(500), distinctUntilChanged())
            .subscribe(() => {
                if (this.depositFoldersControl.value.length > 1) {
                    return;
                }

                if (this.depositFoldersControl.value.length === 1) {
                    this.loadGroupedLayers(this.depositFoldersControl.value[0]);

                    return;
                }

                this.loadGroupedLayers();
            });

        const layersControlSubscription = this.layersControl.valueChanges
            .pipe(distinctUntilChanged())
            .subscribe(async () => {
                if (this.layersControl.value.length !== 1) {
                    this.layerAttributesFormArray.clear();
                    this.layerAttributes.set([]);
                    this.isLayerAttributesExpanded.set(false);

                    return;
                }

                await this.loadLayerAttributes();
            });
        this.subscriptions.push(depositFoldersControlSubscription, layersControlSubscription);
    }

    private disableEditFormIfNotOwned(): void {
        const userId: string = this.securityService.meSignal()!.id;
        const filterBloc: MapFilter | null = this.mapFilterBloc.value();

        if (!filterBloc) {
            return;
        }

        const filterMapOwnerId: string = this.mapFilterBloc.value()!.createdBy.toString();

        if (userId !== filterMapOwnerId) {
            this.filterForm()?.disable({ emitEvent: false });
            this.isEditDisabled.set(true);
        }
    }

    private async expendPanelsForExistingFilter(): Promise<void> {
        this.isEditMode.set(true);

        const filterDepositFolders = this.mapFilterBloc.value()!.conditions.depositFolders;

        if (filterDepositFolders?.length) {
            this.isDepositFoldersExpanded.set(true);

            const displayAtLeastOneDeposit: boolean = this.depositFolders().some((depositFolder) =>
                filterDepositFolders.includes(depositFolder.value),
            );
            if (!displayAtLeastOneDeposit) {
                await this.loadAllDepositFolders();
            }
        }

        await this.loadGroupedLayers(this.depositFoldersControl.value[0]);
        if (this.layersControl.value.length) {
            this.isLayersExpanded.set(true);
            const displayAtLeastOneLayer: boolean = this.displayedLayers().some((layer) =>
                this.layersControl.value.includes(layer.value),
            );

            this.toggleLayerShowAll(!displayAtLeastOneLayer);
            if (this.layersControl.value.length === 1) {
                await this.loadLayerAttributes();
            }
        }

        if (this.mapFilterBloc.value()!.conditions.layerAttributes?.length) {
            this.isLayerAttributesExpanded.set(true);
        }
    }

    private async loadAllDepositFolders(search?: string): Promise<void> {
        const pagination: DepositFolderControllerGetRequestParams = {
            isCaseSensitive: true,
            order: 'ASC',
            orderBy: 'name',
            project: this.projectBloc.value()!.id,
            properties: ['name'],
            take: paginationMaxTake,
            value: search,
        };

        const depositFolders: DepositFolder[] = [];
        let response: DepositFolderControllerGet200Response | null = null;
        do {
            response = await firstValueFrom(
                this.depositFoldersService.depositFolderControllerGet({
                    ...pagination,
                    page: response ? response.page + 1 : 0,
                }),
            );
            depositFolders.push(...response.items);
        } while (response.hasNextPage);
        this.depositFolders.set(
            depositFolders.map((depositFolder: DepositFolder) => ({
                key: depositFolder.name,
                value: depositFolder.id,
            })),
        );
    }

    private async loadGroupedLayers(depositFolderId?: string): Promise<void> {
        const layers = (
            await this.layerService.getAllLayers({
                depositFolder: depositFolderId,
                order: OrderEnum.ASC,
                orderBy: 'name',
                types: [Layer.TypeEnum.TABLE, Layer.TypeEnum.REFERENCE_DATA],
            })
        ).items;

        this.layersByName.set(
            layers.reduce((acc: Map<string, Layer[]>, layer) => {
                if (!acc.has(layer.name)) {
                    acc.set(layer.name, [layer]);

                    return acc;
                }

                acc.set(layer.name, [...acc.get(layer.name)!, layer]);

                return acc;
            }, new Map<string, Layer[]>()),
        );

        const mappedLayers = Array.from(this.layersByName().keys()).map((layerName: string) => ({
            key: layerName,
            value: layerName,
        }));
        this.layers.set(mappedLayers);
        this.displayedLayers.set(mappedLayers.slice(0, this.defaultNumberElementsDisplayed));
    }

    private async loadLayerAttributes(): Promise<void> {
        // as a first approximation, consider that all layers with same name have same attributes so take the first
        const attributes = await this.layerService.getAttributes(
            this.layersByName().get(this.layersControl.value[0])![0],
        );
        this.layerAttributes.set(attributes);
    }

    private async refreshTableAfterPreview(): Promise<void> {
        if (!this.contentLayoutService.isDataTableView()) {
            return;
        }

        await this.mapFilterService.updateAppliedFilters();
        await this.projectEntityTableService.fetchEntities();
    }

    private setButtonDisableReasons(): void {
        const reasons = [];

        if (this.isEditDisabled()) {
            reasons.push(this.translocoService.translate('mapFilter.cannotEditFilterIfNotOwner'));
        }
        if (this.filterForm()!.controls.name.errors?.['required']) {
            reasons.push(this.translocoService.translate('mapFilter.filterNameRequired'));
        }
        if (this.filterForm()!.errors?.['depositFolderOrLayerMandatory']) {
            reasons.push(this.translocoService.translate('mapFilter.depositFolderOrLayerMandatory'));
        }

        this.buttonDisableReasons.set(reasons.join('\n'));
    }

    private updateCompositionRequirement(): void {
        this.mapFilterCompositionService.setFilterAttributeCount(this.layerAttributesFormArray.length);

        const needsComposition = this.layerAttributesFormArray.length >= 2;

        if (!needsComposition) {
            return;
        }

        this.compositionControl.setValidators(Validators.required);
        this.compositionControl.updateValueAndValidity({ emitEvent: true });
    }
}
